import React, { useEffect, useState, useRef } from "react";
import styled, { css } from "styled-components";
import { subscribe, ToastType } from "../shared/Toasts";

const Container = styled.div`
  position: fixed;
  top: 1rem;
  right: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 1100; /* above spinner */
`;

const ToastItem = styled.div`
  min-width: 18rem;
  max-width: 28rem;
  color: var(--color-black);
  background: var(--color-white);
  border-radius: 0.4rem;
  box-shadow: rgba(0, 0, 0, 0.15) 0 4px 16px;
  border-left: 0.4rem solid var(--theme-active-indicator);
  padding: 0.75rem 2.25rem 0.75rem 0.9rem;
  position: relative;
  overflow: hidden;

  ${p =>
    p.$type === ToastType.success &&
    css`
      border-left-color: var(--color-green);
    `}

  ${p =>
    p.$type === ToastType.error &&
    css`
      border-left-color: var(--color-red);
    `}
`;

const Close = styled.button`
  position: absolute;
  right: 0.4rem;
  top: 0.2rem;
  background: transparent;
  border: none;
  color: var(--color-lightgrey);
  font-size: 1.2rem;
  cursor: pointer;
`;

function useToastsFeed() {
  const [items, setItems] = useState([]);
  const timers = useRef(new Map());

  useEffect(() => {
    const unsub = subscribe(toast => {
      setItems(prev => {
        const next = [...prev, toast];
        return next;
      });

      // Auto dismiss: longer for error
      const ttl = toast.type === ToastType.error ? 6000 : 4000;
      const t = setTimeout(() => dismiss(toast.id), ttl);
      timers.current.set(toast.id, t);
    });

    return () => {
      unsub();
      timers.current.forEach(t => clearTimeout(t));
      timers.current.clear();
    };
  }, []);

  function dismiss(id) {
    setItems(prev => prev.filter(t => t.id !== id));
    const t = timers.current.get(id);
    if (t) clearTimeout(t);
    timers.current.delete(id);
  }

  return { items, dismiss };
}

export default function Toasts() {
  const { items, dismiss } = useToastsFeed();
  if (!items.length) return null;

  return (
    <Container role="region" aria-live="polite" aria-label="Notifications">
      {items.map(t => (
        <ToastItem key={t.id} $type={t.type}>
          <Close
            aria-label="Dismiss notification"
            onClick={() => dismiss(t.id)}
          >
            ×
          </Close>
          {t.message}
        </ToastItem>
      ))}
    </Container>
  );
}
